import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
  CardMedia,
  Button,
  Tooltip,
  Stack,
  CircularProgress,
  IconButton,
  Rating,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FacebookIcon from "@mui/icons-material/Facebook";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Checkbox from "@mui/material/Checkbox";

import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import defaultRecipeImg from "../assets/default-recipe.png";
import { Helmet } from "react-helmet-async";
import RecipeReviewsSection from "./RecipeReviewsSection";
import useMediaQuery from "@mui/material/useMediaQuery";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PinterestIcon from "@mui/icons-material/Pinterest";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TelegramIcon from "@mui/icons-material/Telegram";
import XIcon from "@mui/icons-material/X";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ForumIcon from "@mui/icons-material/Forum";
import {
  shareTargetsByNetwork,
  buildRecipeSharePreviewUrl,
  buildCanonicalRecipeUrl,
} from "../utils/share";
import { getRecipeHreflangLinks, FB_LOCALE as FB_LOCALE_MAP } from "../seo/hreflang";
import { useTheme } from "@mui/material/styles";
import { useCountryGate } from "../hooks/useCountryGate";
import { FRONTEND_URL, API_BASE_URL } from "../config/urls";
import { canonicalI18n, pathLangFrom } from "../utils/lang";
import { debugCountry, resolveCountryId } from "../utils/country";
import { useLocationCtx } from "../context/LocationContext";
import {
  buildRecipePath,
  buildRecipePathFromParts,
  buildRecipeSlug,
  getLocalizedField,
} from "../utils/recipeUrl";
import { withCountryPrefix, withQuery } from "./utils/routeHelpers";

import ChangeProductModal from "./shopping/ChangeProductModal";
import InstacartCheckoutButton from "./client/modal/InstacartCheckoutButton";
import FeatureGateDialog from "./modals/FeatureGateDialog";
import { resolveRecipeImage } from "../utils/assets";
import { userHasActiveEntitlement } from "../utils/entitlements";
import { trackEvent } from "../utils/analytics/gtag";

const DEBUG_SHARE_URLS = process.env.REACT_APP_DEBUG_SHARE_URLS === "1";

/** -----------------------------
 *  Country / currency helpers
 *  ----------------------------- */
function getCurrencySymbol(countryId) {
  const c = countryId || "1";
  return c === "2" || c === 2 ? "$" : "€";
}
function convertPrice(price, digits = 2) {
  return Number(price || 0).toFixed(digits);
}
function toNonNegativeMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  return Math.max(0, amount);
}
const getLangKey = (urlLang, i18nLang) =>
  canonicalI18n(urlLang || i18nLang || "en");

/** -----------------------------
 *  US-friendly qty formatting
 *  - We keep backend numbers in g/ml,
 *    but display oz/lb (weight) and fl oz (volume) in US UI.
 *  ----------------------------- */
function isLiquidUnit(unitRaw = "") {
  const u = String(unitRaw || "").toLowerCase();
  return u === "ml" || u === "l" || u === "lt";
}
function formatQtyEUFromGrams(qtyG = 0) {
  const q = Number(qtyG || 0);
  if (!q) return "0 g";
  return q < 1000 ? `${q.toFixed(0)} g` : `${(q / 1000).toFixed(2)} kg`;
}
function formatQtyUSFromGrams(qtyG = 0) {
  const q = Number(qtyG || 0);
  if (!q) return "0 oz";
  const oz = q / 28.349523125;
  if (oz >= 16) {
    const lb = oz / 16;
    return `${lb.toFixed(2)} lb`;
  }
  return `${oz.toFixed(2)} oz`;
}
function formatQtyEUFromMl(qtyMl = 0) {
  const q = Number(qtyMl || 0);
  if (!q) return "0 ml";
  return q < 1000 ? `${q.toFixed(0)} ml` : `${(q / 1000).toFixed(2)} L`;
}
function formatQtyUSFromMl(qtyMl = 0) {
  const q = Number(qtyMl || 0);
  if (!q) return "0 fl oz";
  const floz = q / 29.5735295625;
  return `${floz.toFixed(2)} fl oz`;
}
function formatQtySmart({ qty = 0, unitRaw = "g", isUS = false }) {
  const liquid = isLiquidUnit(unitRaw);
  if (liquid) return isUS ? formatQtyUSFromMl(qty) : formatQtyEUFromMl(qty);
  return isUS ? formatQtyUSFromGrams(qty) : formatQtyEUFromGrams(qty);
}

function supportsPerPersonQty(unitRaw = "") {
  const u = String(unitRaw || "").toLowerCase();
  return ["g", "kg", "ml", "l", "lt"].includes(u);
}

function normalizeGRStoreLocationId(raw) {
  if (raw === null || raw === undefined) return "";
  const s = String(raw).trim();
  if (!s) return "";

  if (s.includes(":")) {
    const [, tail = ""] = s.split(":", 2);
    const n = Number(tail);
    if (Number.isFinite(n) && n > 0) return `gr:${Math.trunc(n)}`;
    return s;
  }

  const n = Number(s);
  if (Number.isFinite(n) && n > 0) return `gr:${Math.trunc(n)}`;
  return s;
}

function getIngredientKey(item) {
  return item?.recipe_product_id ?? `gp:${item?.generic_product_id}`;
}

/** -----------------------------
 *  RecipeDetails
 *  ----------------------------- */
export default function RecipeDetails() {
  const params = useParams();
  const { country: urlLang, slug } = params;
  const idParam = params.id ?? params.recipeId ?? null;
  const [recipe, setRecipe] = useState(null);
  const recipeId = Number(idParam);
  const hasValidId = Number.isFinite(recipeId) && recipeId > 0;
  const id = recipe?.id || (hasValidId ? recipeId : null);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const {
    country: ctxCountry,
    countrySource,
    guestState,
    setCountry,
    refreshGuestState,
  } = useLocationCtx();
  const { needsCountrySelection } = useCountryGate();

  const lang = getLangKey(urlLang, i18n.language);
  const contentLang = i18n.language || lang || "en";
  const apiLang = contentLang === "el" ? "gr" : contentLang;
  if (process.env.REACT_APP_DEBUG_RECIPE_PREVIEW === "1") {
    console.info("[RecipeDetails] params", {
      params,
      idParam,
      recipeId,
      hasValidId,
      lang,
      contentLang,
      apiLang,
    });
  }

  const urlQueryCountry = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    const raw = qs.get("country") || qs.get("country_id") || qs.get("countryId") || "";
    const n = Number(raw);
    return n === 1 || n === 2 ? String(n) : "";
  }, [location.search]);

  const resolvedCountry = resolveCountryId({ locationCountry: ctxCountry, geoCountry: urlQueryCountry });
  const currentRecipePath = `${location.pathname || ""}${location.search || ""}`;
  const recipeReturnTo = currentRecipePath.startsWith("/")
    ? currentRecipePath
    : withCountryPrefix("/", lang);
  const loginPath = withCountryPrefix("/login", lang);
  const registerPath = withCountryPrefix("/register", lang);
  const plansPath = withCountryPrefix("/plans", lang);
  const premiumAuthParams = {
    intent: "premium",
    plan: "monthly",
    returnTo: recipeReturnTo,
  };
  const premiumLoginPath = withQuery(loginPath, premiumAuthParams);
  const premiumRegisterPath = withQuery(registerPath, premiumAuthParams);
  const recipeLoginPath = withQuery(loginPath, { returnTo: recipeReturnTo });
  const recipeRegisterPath = withQuery(registerPath, { returnTo: recipeReturnTo });
  const country = resolvedCountry.id;
  const currencySymbol = getCurrencySymbol(country);
  const isUS = country === "2" || country === 2;
  const guestCountryFromState = guestState?.location?.country || "";
  useEffect(() => {
    debugCountry("recipe.resolve", {
      id: country,
      source: resolvedCountry.source,
      ctxCountry,
      countrySource,
      urlQueryCountry,
    });
  }, [country, resolvedCountry.source, ctxCountry, countrySource, urlQueryCountry]);

  useEffect(() => {
    if (!urlQueryCountry) return;
    if (String(ctxCountry || "") === urlQueryCountry) return;
    if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
      console.info("[RecipeDetails] applying URL country fallback", {
        urlQueryCountry,
        ctxCountry,
        guestCountryFromState: guestCountryFromState || null,
      });
    }
    setCountry && setCountry(urlQueryCountry, "user");
    api.guest
      .mergeState({ location: { country: urlQueryCountry } })
      .then(() => refreshGuestState?.({ respectExplicitSelection: false }))
      .catch(() => {});
  }, [urlQueryCountry, ctxCountry, setCountry, refreshGuestState, guestCountryFromState]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState(4);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateRequire, setGateRequire] = useState("premium");
  const [gateMessage, setGateMessage] = useState("");
  const [followNotice, setFollowNotice] = useState("");
  const [followedByGenericId, setFollowedByGenericId] = useState({});

  const [savingSelected, setSavingSelected] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);
  // ✅ EU (Ελλάδα/Ευρώπη): όπως πριν
  const showItemCost = !isUS;     // per-ingredient cost μόνο EU (δεν αλλάζει η Ελλάδα)

  // ✅ US: ΘΕΛΟΥΜΕ να δείχνουμε ΜΟΝΟ total estimate + note
  const showTotals = true;        // total estimate σε EU + US
  const showCartCost = !isUS;     // EU μόνο (όπως το είχες)
  const [totalRecipeCost, setTotalRecipeCost] = useState(0);
  const [totalPackagesCost, setTotalPackagesCost] = useState(0);
  const [missingCount, setMissingCount] = useState(0);

  const [activeModalItem, setActiveModalItem] = useState(null);
  const [selectedOffersByKey, setSelectedOffersByKey] = useState({});
  const previewRequestIdRef = useRef(0);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const ACTION_BTN_HEIGHT = 44;

  const localizedIngredients = useMemo(
    () => getLocalizedField(recipe, "ingredients", contentLang) || "",
    [recipe, contentLang]
  );
  const localizedDescription = useMemo(
    () => getLocalizedField(recipe, "description", contentLang) || "",
    [recipe, contentLang]
  );
  const localizedInstructions = useMemo(
    () => getLocalizedField(recipe, "instructions", contentLang) || "",
    [recipe, contentLang]
  );
  const fallbackIngredientLines = useMemo(() => {
    if (!localizedIngredients) return [];
    return String(localizedIngredients || "")
      .replace(/\r/g, "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.replace(/^[-•*]\s+/, "").trim())
      .filter(Boolean);
  }, [localizedIngredients]);

  const metaDescription = useMemo(() => {
    const base =
      localizedInstructions ||
      localizedIngredients ||
      recipe?.description ||
      recipe?.instructions ||
      recipe?.ingredients ||
      "";
    const cleaned = String(base || "").replace(/\s+/g, " ").trim();
    if (!cleaned) {
      const name = getLocalizedField(recipe, "name", contentLang) || "Recipe";
      return `${name} | InCooking`;
    }
    return cleaned.length > 160 ? cleaned.slice(0, 160).trim() : cleaned;
  }, [localizedInstructions, localizedIngredients, recipe, contentLang]);

  const heroDescription = useMemo(() => {
    const raw = localizedDescription || recipe?.description || "";
    return String(raw || "").replace(/\s+/g, " ").trim();
  }, [localizedDescription, recipe?.description]);

  const parsedIngredients = useMemo(() => {
    if (!localizedIngredients) return [];
    const lines = String(localizedIngredients || "")
      .replace(/\r/g, "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => !/^Για\s+\d+\s+μερίδ/i.test(line))
      .filter((line) => !/^For\s+\d+\s+serving/i.test(line))
      .map((line) => line.replace(/^[-•*]\s+/, "").trim())
      .filter(Boolean);
    if (lines.length <= 1 && lines[0] && lines[0].includes(",")) {
      return lines[0]
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return lines;
  }, [localizedIngredients]);

  const parsedInstructions = useMemo(() => {
    const raw = String(localizedInstructions || recipe?.instructions || "");
    if (!raw.trim()) return [];
    const steps = raw
      .split(/\s*\d+\.\s+/)
      .map((step) => step.trim())
      .filter(Boolean);
    if (!steps.length) return [];
    return steps.map((step) => ({
      "@type": "HowToStep",
      text: step,
    }));
  }, [localizedInstructions, recipe?.instructions]);

  /** -----------------------------
   *  Geo params for US (zip)
   *  ----------------------------- */
  const geoParams = useMemo(() => {
    const out = {};
    if (isUS) {
      const usZip =
        localStorage.getItem("us_zip") ||
        localStorage.getItem("guest_us_zip") ||
        null;
      if (usZip) out.zip_code = usZip;
    }
    return out;
  }, [isUS]);

  /** -----------------------------
   *  SEO helpers
   *  ----------------------------- */
  function getRecipeName(rec) {
    return getLocalizedField(rec, "name", contentLang) || "";
  }
  function getRecipeDescription(rec) {
    if (!rec) return "";
    const instructions = getLocalizedField(rec, "instructions", contentLang) || "";
    const fallback = getLocalizedField(rec, "ingredients", contentLang) || "";
    const text = instructions || fallback || rec.description || "";
    return String(text || "").replace(/\s+/g, " ").trim();
  }

  const [grStores, setGrStores] = useState([]);
  const [grStoreId, setGrStoreId] = useState(
    normalizeGRStoreLocationId(localStorage.getItem("gr_store_location_id") || "")
  );

  /** -----------------------------
   *  Auth/premium
   *  ----------------------------- */
  useEffect(() => {
    api.users
      .getCurrentUser()
      .then((res) => {
        const user = res.data?.user || null;
        const authenticated = Boolean(res.data?.audience?.is_authenticated && user);
        setIsLoggedIn(authenticated);
        setIsPremium(authenticated ? userHasActiveEntitlement(user) : false);
      })
      .catch(() => {
        setIsLoggedIn(false);
        setIsPremium(false);
      });
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !isPremium) {
      setFollowedByGenericId({});
      return;
    }
    let mounted = true;
    api.followedGeneric
      .list()
      .then((res) => {
        if (!mounted) return;
        const next = {};
        for (const item of res?.data?.items || []) {
          if (item?.generic_product_id) next[String(item.generic_product_id)] = item;
        }
        setFollowedByGenericId(next);
      })
      .catch(() => {
        if (mounted) setFollowedByGenericId({});
      });
    return () => {
      mounted = false;
    };
  }, [isLoggedIn, isPremium]);

  useEffect(() => {
    if (needsCountrySelection) {
      if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
        console.info("[RecipeDetails] stores.search blocked: country not selected");
      }
      return;
    }
    if (isUS) return;
    if (!(country === "1" || country === 1)) return;

    if (process.env.REACT_APP_DEBUG_STORES === "1") {
      console.info("[RecipeDetails] stores.search request", {
        country,
        isUS,
        needsCountrySelection,
      });
    }

    api.stores
      .search({ q: "", limit: 50, country_id: country })
      .then((res) => {
        const arr =
          Array.isArray(res.data?.data) ? res.data.data :
            Array.isArray(res.data) ? res.data :
              [];

        if (process.env.REACT_APP_DEBUG_STORES === "1") {
          console.info("[RecipeDetails] stores.search response", {
            count: arr.length,
          });
        }

        setGrStores(arr);
        debugCountry("recipe.stores", { count: arr.length, country });
      })
      .catch((e) => {
        if (process.env.REACT_APP_DEBUG_STORES === "1") {
          console.info("[RecipeDetails] stores.search error", {
            error: String(e),
          });
        }
        debugCountry("recipe.stores.error", { error: String(e) });
        setGrStores([]);
      });
  }, [isUS, country, grStoreId, needsCountrySelection]);

  useEffect(() => {
    if (!grStoreId) return;
    const normalized = normalizeGRStoreLocationId(grStoreId);
    const storeIds = new Set(
      grStores.map((s) => normalizeGRStoreLocationId(s.external_location_id || s.store_location_id || s.id))
    );
    if (storeIds.size > 0 && !storeIds.has(normalized)) {
      setGrStoreId("");
      localStorage.removeItem("gr_store_location_id");
      return;
    }
    if (normalized !== grStoreId) {
      setGrStoreId(normalized);
      localStorage.setItem("gr_store_location_id", normalized);
    }
  }, [grStoreId, grStores]);



  /** -----------------------------
   *  Fetch recipe by id/slug (with legacy fallback)
   *  ----------------------------- */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setRecipe(null);

      try {
        if (idParam) {
          const res = await api.recipes.getOne(idParam, { params: { lang: apiLang } });
          if (!cancelled) setRecipe(res?.data?.data || null);
          return;
        }

        // ✅ Legacy: fetch by slug or id
        if (slug) {
          const matchId = String(slug).match(/-(\d+)$/);
          const slugId = matchId ? Number(matchId[1]) : null;
          if (slugId) {
            const res = await api.recipes.getOne(slugId, { params: { lang: apiLang } });
            if (!cancelled) setRecipe(res?.data?.data || null);
            return;
          }
          const res = await api.recipes.getOneBySlug(slug, { params: { lang: apiLang } });
          if (!cancelled) setRecipe(res?.data?.data || null);
        }
      } catch (e) {
        if (!cancelled) setRecipe(null);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [idParam, slug, apiLang]);

  /** -----------------------------
   *  Preview cost per recipe (EU = normal, US = estimated/smart)
   *  NOTE:
   *  - We do NOT show store/brand/price-per-unit as "Instacart store price compare" in US mode.
   *  - We only show:
   *      • ingredient
   *      • qty
   *      • estimated ingredient cost (your engine)
   *      • optional "match status" (if available)
   *  ----------------------------- */
  useEffect(() => {
    const requestId = ++previewRequestIdRef.current;
    let cancelled = false;
    const isStale = () => cancelled || requestId !== previewRequestIdRef.current;

    if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
      console.info("[RecipeDetails] country/session diagnostics", {
        needsCountrySelection,
        country,
        countrySource,
        urlQueryCountry,
        guestCountry: guestCountryFromState || null,
        hasGuestIdStorage: (() => {
          try {
            return !!localStorage.getItem("incooking_guest_id");
          } catch {
            return false;
          }
        })(),
      });
    }
    if (!id) {
      if (process.env.REACT_APP_DEBUG_RECIPE_PREVIEW === "1") {
        console.warn("[RecipeDetails] previewForRecipe skipped: invalid id", { id });
      }
      setLoading(false);
      return;
    }
    if (needsCountrySelection) {
      if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
        console.info("[RecipeDetails] previewForRecipe blocked: country not selected");
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setItems([]);
    setSelectedOffersByKey({});
    setTotalRecipeCost(0);
    setTotalPackagesCost(0);
    setMissingCount(0);

    const previewPayload = {
      recipe_id: id,
      household_members: members,
      country_id: country,
      lang: apiLang,
    };
    if (String(country) === "1" && grStoreId) {
      previewPayload.store_location_id = normalizeGRStoreLocationId(grStoreId);
    }
    if (process.env.REACT_APP_DEBUG_RECIPE_PREVIEW === "1") {
      console.info("[RecipeDetails] previewForRecipe payload", previewPayload);
    }

    const fetchPreview = async (retryUnauthorized = true) => {
      try {
        const res = await api.shoppingList.previewForRecipe(previewPayload);
        if (isStale()) return;
        const payload = res?.data || {};
        const rawItems = payload.data || [];
        if (process.env.REACT_APP_DEBUG_RECIPE_PREVIEW === "1") {
          console.info("[RecipeDetails] previewForRecipe response", {
            keys: Object.keys(payload || {}),
            count: Array.isArray(rawItems) ? rawItems.length : 0,
            sample: Array.isArray(rawItems) ? rawItems[0] : null,
          });
        }

        const enhancedItems = rawItems.map((item) => {
          const qty = Number(item.required_quantity ?? item.qty ?? item.quantity ?? 0) || 0;
          const recipeCost =
            item.cost_for_recipe_qty ?? item.cost ?? item.calculated_cost ?? item.theoretical_cost ?? null;
          const basketCost =
            item.pack_cost ?? item.basket_cost ?? null;
          const missing =
            !!item.missing_price ||
            item.no_variant === true ||
            item.has_variant === false ||
            recipeCost == null;

          return {
            ...item,
            qty,
            cost_for_recipe_qty: recipeCost,
            cost: recipeCost,
            pack_cost: basketCost,
            theoretical_cost: recipeCost,
            calculated_cost: recipeCost,
            basket_cost: basketCost,
            missing_price: missing,
          };
        });

        const recipeTotal = toNonNegativeMoney(payload?.totals?.estimated_recipe_cost);
        const packagesTotal = toNonNegativeMoney(payload?.totals?.estimated_packages_cost);
        const missCount = Array.isArray(payload?.missing_price_items)
          ? payload.missing_price_items.length
          : enhancedItems.reduce((sum, it) => sum + (it.missing_price ? 1 : 0), 0);

        if (isStale()) return;
        setItems(enhancedItems);

        if (
          process.env.NODE_ENV !== "production" &&
          (recipeTotal < 0 || packagesTotal < 0)
        ) {
          console.warn("[RecipeDetails] blocked negative totals", {
            recipeTotal,
            packagesTotal,
          });
        }

        // ✅ New totals (for your card/labels)
        setTotalRecipeCost(recipeTotal);
        setTotalPackagesCost(packagesTotal);
        setMissingCount(missCount);

        // Default selected: keep your logic
        const initialSelected = enhancedItems
          .filter((it) => it.generic_product_id)
          .map((it) => String(it.generic_product_id));

        if (isStale()) return;
        setSelectedIds(initialSelected);
        setLoading(false);
      } catch (err) {
        if (isStale()) return;
        const status = err?.response?.status;
        const isUnauthorized = status === 401 || status === 403;
        if (retryUnauthorized && isUnauthorized) {
          if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
            console.warn("[RecipeDetails] preview unauthorized, re-initializing guest session", {
              status,
              country,
              urlQueryCountry,
            });
          }
          try {
            await api.guest.start();
            await api.guest.mergeState({
              location: { country: String(urlQueryCountry || country || "1") },
            });
            await refreshGuestState?.({ respectExplicitSelection: false });
            if (isStale()) return;
            return fetchPreview(false);
          } catch {
            // fall through to final error handling
          }
        }
        if (process.env.REACT_APP_DEBUG_RECIPE_PREVIEW === "1") {
          console.error("[RecipeDetails] previewForRecipe error", err?.response?.data || err);
        }
        setLoading(false);
      }
    };

    fetchPreview(true);
    return () => {
      cancelled = true;
    };
  }, [id, members, country, apiLang, grStoreId, needsCountrySelection, countrySource, urlQueryCountry, guestCountryFromState, refreshGuestState]);


  /** -----------------------------
   *  Share helpers
   *  ----------------------------- */
  const canonicalSlug = recipe ? buildRecipeSlug(recipe, contentLang) : slug;
  const imageUrl = resolveRecipeImage(recipe?.image_url);


  const baseLang = (contentLang === "gr" ? "el" : (contentLang || "en")).split("-")[0];
  const pathLang = pathLangFrom(contentLang);
  const ogLocale =
    (FB_LOCALE_MAP && (FB_LOCALE_MAP[contentLang] || FB_LOCALE_MAP[baseLang])) ||
    "en_US";

  const APP_PUBLIC = FRONTEND_URL;

  const canonicalPath = recipe
    ? buildRecipePath(recipe, contentLang)
    : buildRecipePathFromParts({
      id: idParam,
      name: slug,
      locale: contentLang,
    });
  const absSlugUrl = buildCanonicalRecipeUrl({
    appPublicUrl: APP_PUBLIC,
    canonicalPath,
  });
  const ssrShareUrl = buildRecipeSharePreviewUrl({
    apiBaseUrl: API_BASE_URL,
    lang: pathLang,
    slugOrId: canonicalSlug || String(id || ""),
  });

  useEffect(() => {
    if (!recipe || !canonicalPath) return;
    if (location?.pathname === canonicalPath) return;
    navigate(canonicalPath, { replace: true });
  }, [recipe, canonicalPath, location?.pathname, navigate]);

  const shareTitle = useMemo(
    () => getLocalizedField(recipe, "name", contentLang) || "",
    [recipe, contentLang]
  );

  const share = useMemo(() => {
    if (!recipe?.id) return null;
    const shareTargets = shareTargetsByNetwork({
      title: shareTitle,
      slugUrl: absSlugUrl,
      ssrUrl: ssrShareUrl,
      image: imageUrl,
      facebookAppId: process.env.REACT_APP_FACEBOOK_APP_ID || "",
      appPublicUrl: APP_PUBLIC,
      facebookVersion: recipe?.updated_at || recipe?.id,
    });
    if (DEBUG_SHARE_URLS) {
      console.info("[share] urls", {
        origin: typeof window !== "undefined" ? window.location?.origin : "",
        API_BASE_URL,
        FRONTEND_URL: APP_PUBLIC,
        absSlugUrl,
        ssrShareUrl,
        facebookUrl: shareTargets?.facebook,
      });
    }
    return shareTargets;
  }, [recipe?.id, recipe?.updated_at, shareTitle, absSlugUrl, ssrShareUrl, imageUrl, APP_PUBLIC]);

  const handleShare = (network) => {
    trackEvent("share_click", {
      network,
      recipe_id: recipe?.id,
      recipe_slug: canonicalSlug,
      lang,
    });

    if (!share || !share[network]) return;
    if (DEBUG_SHARE_URLS) {
      console.info("[share] click", {
        network,
        shareUrl: share[network],
      });
    }
    window.open(share[network], "_blank", "noopener,noreferrer");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ssrShareUrl);
    } catch { }
  };

  /** -----------------------------
   *  Selection helpers
   *  ----------------------------- */
  const toggleSelected = (gpId) => {
    if (!gpId) return;
    const gp = String(gpId);
    setSelectedIds((prev) => (prev.includes(gp) ? prev.filter((x) => x !== gp) : [...prev, gp]));
  };

  const selectedCartCost = useMemo(() => {
    return items.reduce((acc, it) => {
      if (!it.generic_product_id) return acc;
      const gp = String(it.generic_product_id);
      if (!selectedIds.includes(gp)) return acc;
      const ingredientKey = getIngredientKey(it);
      const selectedOffer = selectedOffersByKey[ingredientKey];
      const basketCost = toNonNegativeMoney(
        selectedOffer?.pack_cost ??
        selectedOffer?.basket_cost ??
        selectedOffer?.total_cost ??
        it.pack_cost ??
        it.basket_cost
      );
      return acc + basketCost;
    }, 0);
  }, [items, selectedIds, selectedOffersByKey]);

  /** -----------------------------
   *  Smart match modal opener
   *  NOTE:
   *   - Premium-gated
   *   - Uses backend availableStores()
   *   - In US UI we do not display store/brand pricing as "comparison";
   *     the modal can still list options for user selection.
   *  ----------------------------- */
  const handleOpenModalForItem = async (item) => {
    if (isUS) return;
    if (!isLoggedIn) {
      setGateRequire("auth");
      setGateMessage(
        t("premium_feature_change_product") || "Changing product options is available only for Premium users."
      );
      setGateOpen(true);
      return;
    }
    if (!isPremium) {
      setGateRequire("premium");
      setGateMessage(
        t("premium_feature_change_product") || "Changing product options is available only for Premium users."
      );
      setGateOpen(true);
      return;
    }
    if (!item || !item.generic_product_id) return;

    const ingredientKey = getIngredientKey(item);

    try {
      const res = await api.shoppingList.availableStores(item.generic_product_id, {
        ...geoParams,
        qty_needed_base: Number(item.required_quantity ?? item.qty ?? item.quantity ?? 0) || 0,
        base_unit: (item.unit || "g").toLowerCase(),
      });
      const raw = res?.data || {};
      const stores = Array.isArray(raw.stores) ? raw.stores : [];

      const productBaseName =
        (lang === "el" || lang === "gr"
          ? item.product_name_el || item.name_el
          : item.product_name_en || item.name_en) ||
        item.name ||
        item.brand_name ||
        "";

      const currencyCode = isUS ? "USD" : "EUR";

      const offers = stores.map((offRaw) => {
        const off = { ...offRaw };
        const localizedBrand =
          (lang === "el" || lang === "gr"
            ? off.brand_name_el || off.brand_name || off.brand_name_en
            : off.brand_name_en || off.brand_name || off.brand_name_el) || "";
        const localizedProductName =
          (lang === "el" || lang === "gr"
            ? off.product_name_el || off.name_el || off.product_name || off.name
            : off.product_name_en || off.name_en || off.product_name || off.name) ||
          productBaseName ||
          "";
        const normalizedBrand = String(localizedBrand).trim();
        const normalizedProductName = String(localizedProductName).trim();

        const productLabel =
          off.product_full_label ||
          (normalizedBrand && normalizedProductName
            ? normalizedProductName.toLowerCase().startsWith(normalizedBrand.toLowerCase())
              ? normalizedProductName
              : `${normalizedBrand} ${normalizedProductName}`
            : normalizedBrand || normalizedProductName) ||
          (lang === "el" || lang === "gr" ? "Προϊόν" : "Product");

        const storeName =
          off.store_name ||
          off.store_label ||
          off.store_chain ||
          (lang === "el" || lang === "gr" ? "Supermarket" : "Store");

        const packagePrice = Number(off.price || 0) || 0;

        return {
          ...off,
          product_full_label: productLabel,
          store_name: storeName,
          currency: currencyCode,
          package_price: packagePrice || null,
          variant_price: packagePrice || null,
        };
      });

      setActiveModalItem({
        ...item,
        offers,
        activeIngredientKey: ingredientKey,
        currentSelectedVariantId:
          selectedOffersByKey[ingredientKey]?.product_variant_id ||
          item.product_variant_id ||
          null,
      });
    } catch (err) {
      console.error("[RecipeDetails] Error fetching offers:", err);
      setActiveModalItem({
        ...item,
        offers: [],
        activeIngredientKey: ingredientKey,
        currentSelectedVariantId:
          selectedOffersByKey[ingredientKey]?.product_variant_id ||
          item.product_variant_id ||
          null,
      });
    }
  };

  const normalizeSelectedOffer = (offer, row) => {
    if (!offer) return null;

    return {
      ...offer,
      product_variant_id: offer.product_variant_id ?? offer.id ?? null,
      package_price: offer.package_price ?? offer.variant_price ?? offer.price ?? null,
      variant_price: offer.variant_price ?? offer.package_price ?? offer.price ?? null,
      cost_for_recipe_qty: toNonNegativeMoney(
        offer.cost_for_recipe_qty ?? offer.cost ?? offer.calculated_cost ?? offer.theoretical_cost
      ),
      cost: toNonNegativeMoney(
        offer.cost_for_recipe_qty ?? offer.cost ?? offer.calculated_cost ?? offer.theoretical_cost
      ),
      calculated_cost: toNonNegativeMoney(
        offer.cost_for_recipe_qty ?? offer.cost ?? offer.calculated_cost ?? offer.theoretical_cost
      ),
      theoretical_cost: toNonNegativeMoney(
        offer.cost_for_recipe_qty ?? offer.cost ?? offer.calculated_cost ?? offer.theoretical_cost
      ),
      pack_cost: toNonNegativeMoney(
        offer.pack_cost ?? offer.basket_cost ?? offer.total_cost ?? offer.cost_for_recipe_qty
      ),
      basket_cost: toNonNegativeMoney(
        offer.pack_cost ?? offer.basket_cost ?? offer.total_cost ?? offer.cost_for_recipe_qty
      ),
      brand_name:
        offer.brand_name ||
        offer.brand_name_el ||
        offer.brand_name_en ||
        row?.brand_name ||
        null,
    };
  };

  const getEffectiveOfferForItem = (item) => {
    const ingredientKey = getIngredientKey(item);
    const selectedOffer = selectedOffersByKey[ingredientKey];
    if (!selectedOffer) return item;

    return {
      ...item,
      ...selectedOffer,
      ingredient_key: ingredientKey,
      qty: item.qty,
      required_quantity: item.required_quantity,
      generic_product_id: item.generic_product_id,
      recipe_product_id: item.recipe_product_id,
    };
  };

  const getIngredientTitle = (item) =>
    (lang === "el" || lang === "gr"
      ? item.generic_product_name_el || item.name_el || item.product_name_el
      : item.generic_product_name_en || item.name_en || item.product_name_en) ||
    item.name ||
    "";

  const getSelectedVariantLabel = (item) => {
    const fullLabel = String(item.product_full_label || "").trim();
    if (fullLabel) return fullLabel;

    const localizedBrand = String(
      (lang === "el" || lang === "gr"
        ? item.brand_name_el || item.brand_name || item.brand_name_en
        : item.brand_name_en || item.brand_name || item.brand_name_el) || ""
    ).trim();
    const localizedProductName = String(
      (lang === "el" || lang === "gr"
        ? item.product_name_el || item.name_el || item.product_name || item.name
        : item.product_name_en || item.name_en || item.product_name || item.name) || ""
    ).trim();
    const packageText = String(
      item.package_size_text || item.package_label || ""
    ).trim();

    const baseLabel =
      localizedBrand && localizedProductName
        ? localizedProductName.toLowerCase().startsWith(localizedBrand.toLowerCase())
          ? localizedProductName
          : `${localizedBrand} ${localizedProductName}`
        : localizedBrand || localizedProductName;

    if (!baseLabel) return "";
    if (!packageText) return baseLabel;
    if (baseLabel.toLowerCase().includes(packageText.toLowerCase())) return baseLabel;
    return `${baseLabel} ${packageText}`;
  };

  const getEffectiveStoreLabel = (item) =>
    item.store_label ||
    item.store_name ||
    item.store_chain ||
    item.store ||
    "";

  const getEffectiveUnitPriceLabel = (item) => {
    const pricePerKg = Number(item.price_per_kg);
    const pricePerLiter = Number(item.price_per_liter);
    const pricePerPiece = Number(item.price_per_piece);

    if (Number.isFinite(pricePerKg) && pricePerKg > 0) {
      return `${convertPrice(pricePerKg)}${currencySymbol} /kg`;
    }
    if (Number.isFinite(pricePerLiter) && pricePerLiter > 0) {
      return `${convertPrice(pricePerLiter)}${currencySymbol} /L`;
    }
    if (Number.isFinite(pricePerPiece) && pricePerPiece > 0) {
      return `${convertPrice(pricePerPiece)}${currencySymbol} /piece`;
    }

    const fallbackUnitPrice = Number(item.price);
    if (Number.isFinite(fallbackUnitPrice) && fallbackUnitPrice > 0) {
      const unitRaw = (item.normalized_unit || item.unit || "").toLowerCase();
      if (unitRaw === "piece" || unitRaw === "τεμ") {
        return `${convertPrice(fallbackUnitPrice)}${currencySymbol} /piece`;
      }
      if (isLiquidUnit(unitRaw)) {
        return `${convertPrice(fallbackUnitPrice)}${currencySymbol} /L`;
      }
      return `${convertPrice(fallbackUnitPrice)}${currencySymbol} /kg`;
    }

    return "--";
  };

  const getEffectivePackPriceLabel = (item) => {
    const packagePriceValue =
      item.package_price !== undefined && item.package_price !== null
        ? Number(item.package_price)
        : item.variant_price !== undefined && item.variant_price !== null
          ? Number(item.variant_price)
          : null;

    return packagePriceValue !== null && Number.isFinite(packagePriceValue)
      ? `${convertPrice(packagePriceValue)}${currencySymbol} / pack`
      : "--";
  };

  const getEffectiveCostValue = (item) =>
    toNonNegativeMoney(
      item.cost_for_recipe_qty ?? item.cost ?? item.calculated_cost ?? item.theoretical_cost
    );

  const handleFollowProduct = async (item) => {
    if (!item?.generic_product_id) return;
    if (!isLoggedIn) {
      setGateRequire("auth");
      setGateMessage(
        t("follow_price_guest_tease") || "Create an account to follow product prices and unlock Premium alerts."
      );
      setGateOpen(true);
      return;
    }
    if (!isPremium) {
      setGateRequire("premium");
      setGateMessage(
        t("follow_price_premium_tease") || "Premium lets you follow products and react to supermarket price changes."
      );
      setGateOpen(true);
      return;
    }

    const genericKey = String(item.generic_product_id);
    const existing = followedByGenericId[genericKey] || null;
    const previous = followedByGenericId;
    setFollowNotice("");
    setFollowedByGenericId((current) => {
      const next = { ...current };
      if (existing) delete next[genericKey];
      else next[genericKey] = { id: `pending-${genericKey}`, generic_product_id: item.generic_product_id };
      return next;
    });
    try {
      if (existing) {
        await api.followedGeneric.unfollow(item.generic_product_id);
        setFollowNotice(t("follow_removed_success") || "Removed from price watch.");
      } else {
        const res = await api.followedGeneric.follow({ generic_product_id: item.generic_product_id });
        setFollowedByGenericId((current) => ({
          ...current,
          [genericKey]: res?.data?.item,
        }));
        setFollowNotice(t("watchlist_added_success") || "Added to your price watchlist.");
      }
    } catch (err) {
      setFollowedByGenericId(previous);
      setFollowNotice(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (t("watchlist_add_error") || "Failed to update watchlist.")
      );
    }
  };

  /** -----------------------------
   *  Save selected from recipe -> shopping list
   *  ----------------------------- */
  const handleSaveSelectedFromRecipe = async () => {
    if (!isLoggedIn) {
      setShowLoginDialog(true);
      return;
    }

    if (!id || selectedIds.length === 0) {
      setSaveMsg(
        lang === "el" || lang === "gr"
          ? t("select_at_least_one_product") || "Διάλεξε τουλάχιστον 1 προϊόν."
          : t("select_at_least_one_product") || "Select at least 1 product."
      );
      return;
    }

    try {
      setSavingSelected(true);
      setSaveMsg("");

      trackEvent("save_selected_to_list", {
        recipe_id: id,
        selected_count: selectedIds?.length || 0,
        members,
        country,
        lang,
      });

      const res = await api.shoppingList.saveSelected({
        context: "recipe",
        recipe_id: id,
        servings: members,
        include_generic_ids: selectedIds,
        lang: apiLang,
      });

      if (res.data?.success) {
        setSaveMsg(
          lang === "el" || lang === "gr"
            ? t("saved_selected_to_list") || "Αποθηκεύτηκε η λίστα με τα επιλεγμένα προϊόντα."
            : t("saved_selected_to_list") || "Saved selected items to your shopping list."
        );
        navigate(`/${lang}/client/select-shopping-list`);
      } else {
        setSaveMsg(res.data?.error || (t("save_failed") || "Save failed"));
      }
    } catch (e) {
      setSaveMsg(t("save_error") || "Save error");
    } finally {
      setSavingSelected(false);
    }
  };

  /** -----------------------------
   *  Login dialog
   *  ----------------------------- */
  const LoginDialog = () => (
    <Dialog open={showLoginDialog} onClose={() => setShowLoginDialog(false)}>
      <DialogTitle>{t("login_required") || "Απαιτείται σύνδεση"}</DialogTitle>
      <DialogContent>
        <Typography>
          {t("shopping_list_guest_cta") ||
            "Για να αποθηκεύσετε και να διαχειριστείτε τη λίστα αγορών ή να προσθέσετε συνταγές στη λίστα, δημιουργήστε έναν λογαριασμό!"}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setShowLoginDialog(false);
            navigate(recipeRegisterPath);
          }}
        >
          {t("cta_register_button") || "Εγγραφή"}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            setShowLoginDialog(false);
            navigate(recipeLoginPath);
          }}
        >
          {t("login") || "Σύνδεση"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  /** -----------------------------
   *  Grid colors by theme
   *  ----------------------------- */
  const gridBgHeader = theme.palette.mode === "dark" ? "#23272e" : theme.palette.grey[200];
  const gridBgEven = theme.palette.mode === "dark" ? "#1e2023" : "#fff";
  const gridBgOdd = theme.palette.mode === "dark" ? "#23272e" : theme.palette.grey[50];
  const gridTextColor = theme.palette.mode === "dark" ? "#fff" : "#222";

  if (loading || !recipe)
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );

  /** -----------------------------
   *  UI labels (i18n keys with fallback)
   *  ----------------------------- */
  const L = {
    ingredients: t("ingredients") || "Υλικά",
    ingredient: t("ingredient") || "Υλικό",
    qty: t("qty") || "Ποσότητα",
    store: t("store") || "Κατάστημα",
    pricePerUnit: t("price_per_unit") || "Τιμή ανά μονάδα",
    cost: t("cost") || "Κόστος",
    instructions: t("instructions") || "Οδηγίες Εκτέλεσης",
    totalFullCost: t("total_full_cost") || "Συνολικό Κόστος",
    selectedCartCost: t("selected_products_cost_cart") || "Κόστος επιλεγμένων προϊόντων (καλάθι)",
    chooseProduct: t("choose_product") || (lang === "el" || lang === "gr" ? "Επιλογή προϊόντος" : "Choose product"),
    notFoundArea:
      t("not_found_in_your_area") ||
      (lang === "el" || lang === "gr"
        ? "Δεν βρέθηκε προϊόν στην περιοχή σας"
        : "Product not available in your area"),
    usEstimateNoteTitle: t("us_estimate_note_title") || (lang === "el" || lang === "gr" ? "Σημείωση (ΗΠΑ)" : "US note"),
    usEstimateNoteBody:
      t("us_estimate_note_body") ||
      (lang === "el" || lang === "gr"
        ? "Οι τιμές είναι εκτιμήσεις και μπορεί να διαφέρουν στο Instacart checkout. Δεν εμφανίζονται τιμές/μάρκες/καταστήματα ως σύγκριση τιμών πριν το checkout."
        : "Prices are estimates and may differ at Instacart checkout. We don’t display store/brand pricing comparisons before checkout."),
  };

  return (
    <Box
      sx={{
        maxWidth: 750,
        width: "100%",
        mx: "auto",
        bgcolor: "background.paper",
        my: 2,
        borderRadius: 4,
        boxShadow: 3,
        p: { xs: 1, sm: 3 },
        overflowX: "hidden",
      }}
    >
      <Helmet>
        <title>{`${getRecipeName(recipe)} | InCooking`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={absSlugUrl} />

        <meta property="og:title" content={getRecipeName(recipe)} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="inCooking" />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:url" content={absSlugUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:secure_url" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getRecipeName(recipe)} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Recipe",
            mainEntityOfPage: absSlugUrl,
            inLanguage: (contentLang || "en").replace("_", "-"),
            name: getRecipeName(recipe),
            description: metaDescription,
            image: [imageUrl],
            author: { "@type": "Organization", name: "inCooking" },
            ...((Number(recipe?.avg_rating) > 0 && Number(recipe?.rating_count) > 0) ||
              (Number(recipe?.review_stats?.rating) > 0 && Number(recipe?.review_stats?.count) > 0)
              ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: Number(recipe?.avg_rating ?? recipe?.review_stats?.rating).toFixed(1),
                  ratingCount: Number(recipe?.rating_count ?? recipe?.review_stats?.count),
                },
              }
              : {}),
            prepTime: recipe.prep_time ? `PT${recipe.prep_time}M` : undefined,
            recipeYield: recipe.portions || "4",
            recipeIngredient: parsedIngredients,
            recipeInstructions: parsedInstructions.length
              ? parsedInstructions
              : (localizedInstructions
                ? [{ "@type": "HowToStep", text: localizedInstructions }]
                : []),
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "inCooking", item: `${APP_PUBLIC}/${pathLang}/` },
              {
                "@type": "ListItem",
                position: 2,
                name: lang === "el" || lang === "gr" ? "Συνταγές" : "Recipes",
                item: `${APP_PUBLIC}/${pathLang}/search-recipes`,
              },
              { "@type": "ListItem", position: 3, name: getRecipeName(recipe), item: absSlugUrl },
            ],
          })}
        </script>
        {getRecipeHreflangLinks(recipe, "en")}
      </Helmet>



      {/* Title & Ratings */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          mb: 2,
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ mb: 1 }}>
          {getRecipeName(recipe)}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Rating value={Number(recipe.review_stats?.rating) || 0} precision={0.1} readOnly size="medium" />
          <Typography sx={{ fontWeight: 500 }}>
            {recipe.review_stats?.rating ? Number(recipe.review_stats.rating).toFixed(1) : "-"} / 5
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 15 }}>({recipe.review_stats?.count || 0})</Typography>
          <Button
            href="#reviews-section"
            sx={{ textTransform: "none", fontWeight: 700, fontSize: 15, mt: isMobile ? 1 : 0 }}
            variant="text"
          >
            {t("see_reviews")}
          </Button>
        </Box>
      </Box>

      {followNotice ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setFollowNotice("")}>
          {followNotice}
        </Alert>
      ) : null}

      {/* {showAds && (
        <Box sx={{ my: 2 }}>
          <AdSenseAd slot="2080997069" isPremium={isPremium} />
        </Box>
      )} */}

      {/* Image */}
      <CardMedia
        component="img"
        image={imageUrl}
        alt={getRecipeName(recipe)}
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = defaultRecipeImg;
        }}
        sx={{ width: "100%", maxHeight: isMobile ? 180 : 300, objectFit: "cover", borderRadius: 3 }}
      />


      {heroDescription ? (
        <Typography color="text.secondary" sx={{ mb: 2, fontSize: { xs: 15, sm: 18 } }}>
          {heroDescription}
        </Typography>
      ) : null}

      {/* {showAds && (
        <Box sx={{ my: 2 }}>
          <AdSenseAd slot="2080997069" isPremium={isPremium} />
        </Box>
      )} */}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        {recipe.difficulty && (
          <Chip label={`${t("recipes.search.difficulty")}: ${t(`difficulty.${recipe.difficulty}`, { defaultValue: recipe.difficulty })}`} />
        )}
        {recipe.prep_time && <Chip label={`${t("prep_time_label")}: ${recipe.prep_time}′`} />}
        {recipe.calories && <Chip label={t("recipes.search.calories") + ": " + recipe.calories} />}
      </Stack>

      {/* Servings */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography>
          {t("ingredients_for_x_people", { count: members }) || `Υλικά για ${members} άτομα:`}
        </Typography>
        <IconButton size="small" onClick={() => setMembers(Math.max(1, members - 1))} disabled={members <= 1}>
          <RemoveIcon />
        </IconButton>
        <TextField
          size="small"
          type="number"
          value={members}
          onChange={(e) => setMembers(Math.max(1, parseInt(e.target.value) || 1))}
          sx={{ width: 60, mx: 1 }}
          inputProps={{ min: 1, max: 20, style: { textAlign: "center" } }}
        />
        <IconButton size="small" onClick={() => setMembers(Math.min(20, members + 1))}>
          <AddIcon />
        </IconButton>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h5" sx={{ mb: 1 }}>
        {L.ingredients}
      </Typography>

      <Typography
        variant="body2"
        sx={{ mb: 2, color: "text.secondary", fontStyle: "italic" }}
      >
        {t(
          "recipe_details.balanced_portions_note",
          "Quantities are calculated for balanced portions without unnecessary waste."
        )}
      </Typography>

      {/* US compliance note */}
      {isUS && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <b>{L.usEstimateNoteTitle}</b>
          <div style={{ marginTop: 6 }}>{L.usEstimateNoteBody}</div>
        </Alert>
      )}

      {!isUS && (country === "1" || country === 1) && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 700 }}>
            {t("select_supermarket") || "Σούπερ μάρκετ"}:
          </Typography>

          <FormControl size="small" sx={{ minWidth: 260 }}>
            <Select
              value={grStoreId}
              onChange={(e) => {
                const v = normalizeGRStoreLocationId(e.target.value || "");
                setGrStoreId(v);
                if (v) localStorage.setItem("gr_store_location_id", v);
                else localStorage.removeItem("gr_store_location_id");
              }}

              displayEmpty
            >
              <MenuItem value="">
                {t("all_stores_cheapest") || "Όλα (πιο φθηνό)"}
              </MenuItem>

              {grStores.map((s) => {
                const value = normalizeGRStoreLocationId(s.external_location_id || s.store_location_id || s.id); // fallback μόνο αν δεν έχεις external
                return (
                  <MenuItem key={value} value={value}>
                    {s.label || s.name || s.store_name || value}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* INGREDIENTS LIST */}
      <Box sx={{ width: "100%", mb: 2 }}>
        {items.length === 0 && fallbackIngredientLines.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {fallbackIngredientLines.map((line, idx) => (
              <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", py: 0.6 }}>
                <Typography sx={{ fontWeight: 600 }}>{line}</Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {t("estimated_cost_unavailable", "Estimated cost unavailable")}
                </Typography>
              </Box>
            ))}
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
              {t("costs_require_pricing_data", "Pricing data unavailable for this recipe.")}
            </Typography>
          </Box>
        )}
        {/* DESKTOP */}
        {!isMobile ? (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isUS ? "2fr 1fr" : "2fr 1fr 2fr 1fr 1fr",
                gap: 1,
                fontWeight: 700,
                bgcolor: gridBgHeader,
                color: gridTextColor,
                py: 1.3,
                px: 1,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderBottom: `1px solid #333`,
              }}
            >
              <span>{L.ingredient}</span>
              <span style={{ textAlign: "right" }}>{L.qty}</span>

              {!isUS && <span>{L.store}</span>}
              {!isUS && <span style={{ textAlign: "right" }}>{L.pricePerUnit}</span>}
              {!isUS && <span style={{ textAlign: "right" }}>{L.cost}</span>}
            </Box>

            {items.map((rawItem, i) => {
              const item = getEffectiveOfferForItem(rawItem);
              const ingredientKey = getIngredientKey(rawItem);
              const ingredientTitle = getIngredientTitle(rawItem);
              const selectedVariantLabel = getSelectedVariantLabel(item);

              const qty = Number(item.qty || 0);
              const unitRaw = (item.package_normalized_unit || item.normalized_unit || item.unit || "g").toLowerCase();

              const qtyLabel = formatQtySmart({ qty, unitRaw, isUS });
              const perPersonQtyLabel =
                members > 0 && supportsPerPersonQty(unitRaw)
                  ? formatQtySmart({ qty: qty / members, unitRaw, isUS })
                  : "";

              const storeLabelRaw = getEffectiveStoreLabel(item);
              const priceLabel = getEffectiveUnitPriceLabel(item);
              const costValue = getEffectiveCostValue(item);
              const costLabel = `${convertPrice(costValue)}${currencySymbol}`;
              const packagePriceLabel = getEffectivePackPriceLabel(item);
              const basketEstimateLabel =
                item.basket_cost !== undefined && item.basket_cost !== null
                  ? `${convertPrice(Number(item.basket_cost) || 0)}${currencySymbol}`
                  : "--";
              const adjustedEstimateLabel =
                item.adjusted_cost !== undefined && item.adjusted_cost !== null
                  ? `${convertPrice(Number(item.adjusted_cost) || 0)}${currencySymbol}`
                  : basketEstimateLabel;
              const inventoryUsed = Number(item.used_from_inventory || 0);
              const missingQty = Number(item.missing_quantity ?? item.required_quantity ?? 0);
              const isFollowed = Boolean(followedByGenericId[String(item.generic_product_id)]);

              return (
                <Box
                  key={ingredientKey}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isUS ? "2fr 1fr" : "2fr 1fr 2fr 1fr 1fr",
                    gap: 1,
                    alignItems: "center",
                    borderBottom: `1px solid #eee`,
                    bgcolor: i % 2 === 0 ? gridBgEven : gridBgOdd,
                    py: 1,
                    px: 1,
                    color: gridTextColor,
                  }}
                >
                  {/* Ingredient / Product column */}
                  <span style={{ fontWeight: 600 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Selection checkbox stays (US checkout needs it) */}
                      <Checkbox
                        size="small"
                        checked={item.generic_product_id ? selectedIds.includes(String(item.generic_product_id)) : false}
                        onChange={() => toggleSelected(item.generic_product_id)}
                        sx={{ p: 0.5 }}
                      />

                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                          {ingredientTitle}
                        </Typography>

                        {!isUS && selectedVariantLabel && (
                          <Typography
                            component="span"
                            sx={{ display: "block", fontSize: 12, opacity: 0.85, fontWeight: 400 }}
                          >
                            {selectedVariantLabel}
                          </Typography>
                        )}

                        {/* US: optional "match" badge if backend provides fields */}
                        {isUS && (
                          <Typography
                            component="span"
                            sx={{ display: "block", fontSize: 12, opacity: 0.75, fontWeight: 500 }}
                          >
                            {item.match_status
                              ? (t(`match_status_${item.match_status}`) || `Match: ${item.match_status}`)
                              : (t("match_status_estimated") || "Match: estimated")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </span>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {qtyLabel}
                    </Typography>
                    {perPersonQtyLabel ? (
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                        {t("recipe_details.per_person_qty", { qty: perPersonQtyLabel, defaultValue: "≈ {{qty}} / person" })}
                      </Typography>
                    ) : null}
                  </Box>

                  {/* EU store + choose product */}
                  {!isUS && (
                    <span>
                      {storeLabelRaw ? (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            fontWeight: 500,
                            color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
                          }}
                        >
                          <StorefrontIcon sx={{ fontSize: 18 }} />
                          <span>{storeLabelRaw}</span>

                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleOpenModalForItem(item)}
                            sx={{ textTransform: "none", fontSize: 11, ml: 1, px: 1, py: 0.2 }}
                          >
                            {L.chooseProduct}
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: "error.main", fontWeight: 700 }}>
                          {L.notFoundArea}
                        </Typography>
                      )}
                    </span>
                  )}

                  {/* EU price per unit */}
                  {!isUS && (
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {packagePriceLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", opacity: 0.82 }}>
                        {priceLabel}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ textAlign: "right" }}>
                    {!isUS ? (
                      <>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {costLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", color: "success.main", fontWeight: 700 }}>
                        {(t("cost_estimate.adjusted_cost") || "Σου κοστίζει τώρα")}: {adjustedEstimateLabel}
                      </Typography>
                      <Tooltip title={t("basket_cost") || "Κόστος συσκευασιών"}>
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, opacity: 0.8 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            {(t("basket_cost") || "Κόστος συσκευασιών")}: {basketEstimateLabel}
                          </Typography>
                        </Box>
                      </Tooltip>
                      {inventoryUsed > 0 ? (
                        <Typography variant="caption" sx={{ display: "block", color: "success.main", fontWeight: 700 }}>
                          {`✔ ${t("inventory_used") || "Έχεις"} ${formatQtySmart({ qty: inventoryUsed, unitRaw: (item.required_unit || item.unit || "g").toLowerCase(), isUS })}`}
                        </Typography>
                      ) : null}
                      {missingQty > 0 ? (
                        <Typography variant="caption" sx={{ display: "block", color: "warning.main", fontWeight: 700 }}>
                          {`❌ ${t("missing") || "Λείπει"} ${formatQtySmart({ qty: missingQty, unitRaw: (item.required_unit || item.unit || "g").toLowerCase(), isUS })}`}
                        </Typography>
                      ) : null}
                      </>
                    ) : null}
                    {item.generic_product_id ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleFollowProduct(item)}
                        sx={{ mt: !isUS ? 0.5 : 0, textTransform: "none", px: 0 }}
                      >
                        {isFollowed
                          ? (t("unfollow_price") || "Διακοπή παρακολούθησης")
                          : (t("follow_price") || "Παρακολούθηση τιμής")}
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </>
        ) : (
          /** MOBILE */
          <Stack spacing={2}>
            {items.map((rawItem, i) => {
              const item = getEffectiveOfferForItem(rawItem);
              const ingredientKey = getIngredientKey(rawItem);
              const ingredientTitle = getIngredientTitle(rawItem);
              const selectedVariantLabel = getSelectedVariantLabel(item);

              const qty = Number(item.qty || 0);
              const unitRaw = (item.package_normalized_unit || item.normalized_unit || item.unit || "g").toLowerCase();
              const qtyLabel = formatQtySmart({ qty, unitRaw, isUS });
              const perPersonQtyLabel =
                members > 0 && supportsPerPersonQty(unitRaw)
                  ? formatQtySmart({ qty: qty / members, unitRaw, isUS })
                  : "";

              const costValue = getEffectiveCostValue(item);
              const costLabel = `${convertPrice(costValue)}${currencySymbol}`;
              const packagePriceLabel = getEffectivePackPriceLabel(item);
              const basketEstimateLabel =
                item.basket_cost !== undefined && item.basket_cost !== null
                  ? `${convertPrice(Number(item.basket_cost) || 0)}${currencySymbol}`
                  : "--";
              const adjustedEstimateLabel =
                item.adjusted_cost !== undefined && item.adjusted_cost !== null
                  ? `${convertPrice(Number(item.adjusted_cost) || 0)}${currencySymbol}`
                  : basketEstimateLabel;
              const inventoryUsed = Number(item.used_from_inventory || 0);
              const missingQty = Number(item.missing_quantity ?? item.required_quantity ?? 0);
              const isFollowed = Boolean(followedByGenericId[String(item.generic_product_id)]);

              const storeLabelRaw = getEffectiveStoreLabel(item);
              const priceLabel = getEffectiveUnitPriceLabel(item);

              return (
                <Box
                  key={ingredientKey}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    boxShadow: 1,
                    bgcolor: i % 2 === 0 ? gridBgEven : gridBgOdd,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.8,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={item.generic_product_id ? selectedIds.includes(String(item.generic_product_id)) : false}
                      onChange={() => toggleSelected(item.generic_product_id)}
                      sx={{ p: 0.5 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={800}>{ingredientTitle}</Typography>

                      {!isUS && selectedVariantLabel && (
                        <Typography variant="body2" sx={{ opacity: 0.85, fontSize: 12 }}>
                          {selectedVariantLabel}
                        </Typography>
                      )}

                      {/* US: match status */}
                      {isUS && (
                        <Typography variant="body2" sx={{ opacity: 0.75, fontSize: 12, fontWeight: 600 }}>
                          {item.match_status
                            ? (t(`match_status_${item.match_status}`) || `Match: ${item.match_status}`)
                            : (t("match_status_estimated") || "Match: estimated")}
                        </Typography>
                      )}
                    </Box>

                    {/* EU only: choose product button on mobile */}
                    {!isUS && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleOpenModalForItem(item)}
                        sx={{ textTransform: "none", fontSize: 11 }}
                      >
                        {L.chooseProduct}
                      </Button>
                    )}
                  </Box>

                  {/* EU store row */}
                  {!isUS && (
                    <>
                      {storeLabelRaw ? (
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
                            fontWeight: 600,
                          }}
                        >
                          <StorefrontIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          {storeLabelRaw}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: "error.main", fontWeight: 700 }}>
                          {L.notFoundArea}
                        </Typography>
                      )}
                    </>
                  )}

                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Box>
                      <Typography variant="body2">
                        {L.qty}: <strong>{qtyLabel}</strong>
                      </Typography>
                      {perPersonQtyLabel ? (
                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                          {t("recipe_details.per_person_qty", { qty: perPersonQtyLabel, defaultValue: "≈ {{qty}} / person" })}
                        </Typography>
                      ) : null}
                    </Box>
                    {showItemCost && (
                      <Typography variant="body2">
                        {L.cost}: <strong>{costLabel}</strong>
                      </Typography>
                    )}

                  </Stack>

                  {/* EU price per unit */}
                  <Stack spacing={0.3}>
                    {!isUS ? (
                      <>
                      <Typography variant="body2">
                        {t("pack_price_label") || "Pack price"}: {packagePriceLabel}
                      </Typography>
                      <Typography variant="body2">
                        {L.pricePerUnit}: {priceLabel}
                      </Typography>
                      <Typography variant="body2">
                        {t("recipe_qty_cost_label") || "Recipe qty cost"}: <strong>{costLabel}</strong>
                      </Typography>
                      <Typography variant="body2">
                        {t("basket_cost") || "Κόστος συσκευασιών"}: <strong>{basketEstimateLabel}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ color: "success.main", fontWeight: 700 }}>
                        {t("cost_estimate.adjusted_cost") || "Σου κοστίζει τώρα"}: <strong>{adjustedEstimateLabel}</strong>
                      </Typography>
                      {inventoryUsed > 0 ? (
                        <Typography variant="body2" sx={{ color: "success.main" }}>
                          {`✔ ${t("inventory_used") || "Έχεις"} ${formatQtySmart({ qty: inventoryUsed, unitRaw: (item.required_unit || item.unit || "g").toLowerCase(), isUS })}`}
                        </Typography>
                      ) : null}
                      {missingQty > 0 ? (
                        <Typography variant="body2" sx={{ color: "warning.main" }}>
                          {`❌ ${t("missing") || "Λείπει"} ${formatQtySmart({ qty: missingQty, unitRaw: (item.required_unit || item.unit || "g").toLowerCase(), isUS })}`}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" sx={{ opacity: 0.78 }}>
                        {t("package_rounding_info") || "We buy in packages. Cost uses package rounding."}
                      </Typography>
                      </>
                    ) : null}
                    {item.generic_product_id ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleFollowProduct(item)}
                        sx={{ alignSelf: "flex-start", textTransform: "none", px: 0 }}
                      >
                        {isFollowed
                          ? (t("unfollow_price") || "Διακοπή παρακολούθησης")
                          : (t("follow_price") || "Παρακολούθηση τιμής")}
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* {showAds && (
        <Box sx={{ my: 2 }}>
          <AdSenseAd slot="2080997069" isPremium={isPremium} />
        </Box>
      )} */}

      {/* Totals */}
      {showTotals && (
        <Box sx={{ mb: 2, mt: 1 }}>
          <Tooltip title={t("estimated_cost") || "Εκτίμηση κόστους"}>
            <Box>
              <Typography fontWeight={700} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {t("cost_estimate.recipe_cost") || "Κόστος συνταγής"}:
                <span style={{ fontWeight: 900 }}>
                  ~{convertPrice(totalRecipeCost)} {currencySymbol}
                </span>
              </Typography>

              <Typography fontWeight={700} sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.3 }}>
                {t("cost_estimate.packages_cost") || "Κόστος συσκευασιών"}:
                <span style={{ fontWeight: 900 }}>
                  ~{convertPrice(totalPackagesCost)} {currencySymbol}
                </span>
              </Typography>

              <Typography fontWeight={700} sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.3, color: "success.main" }}>
                {t("cost_estimate.adjusted_cost") || "Σου κοστίζει τώρα"}:
                <span style={{ fontWeight: 900 }}>
                  ~{convertPrice(selectedCartCost || 0)} {currencySymbol}
                </span>
              </Typography>

              {missingCount > 0 && (
                <Typography sx={{ mt: 0.4, fontSize: 13, color: "warning.main", fontWeight: 700 }}>
                  {t("cost_estimate.missing_prices", { count: missingCount }) ||
                    `Λείπουν τιμές για ${missingCount} υλικά.`}
                </Typography>
              )}

              {isUS && (
                <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>
                  {t("us_estimate_note_body") ||
                    "Εκτίμηση βάσει μέσων τιμών/συσκευασιών — όχι τελική τιμή. Το τελικό ποσό εμφανίζεται στο checkout συνεργάτη."}
                </Typography>
              )}
            </Box>
          </Tooltip>

          {/* EU cart cost όπως πριν */}
          {showCartCost && (
            <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.secondary" }}>
              {L.selectedCartCost}: <b>{convertPrice(selectedCartCost)} {currencySymbol}</b>
            </Typography>
          )}
        </Box>
      )}

      {/* Actions */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mt: 2, mb: 2, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}
      >
        <Button
          variant="contained"
          startIcon={<ShoppingBasketIcon />}
          onClick={handleSaveSelectedFromRecipe}
          disabled={savingSelected || selectedIds.length === 0}
          sx={{
            fontWeight: 800,
            whiteSpace: "nowrap",
            minWidth: { sm: 240 },
            borderRadius: 2,
            minHeight: ACTION_BTN_HEIGHT,
            py: 1,
          }}
        >
          {savingSelected
            ? lang === "el" || lang === "gr"
              ? t("saving") || "Αποθήκευση..."
              : t("saving") || "Saving..."
            : lang === "el" || lang === "gr"
              ? t("save_selected") || "Αποθήκευση επιλεγμένων"
              : t("save_selected") || "Save selected"}
        </Button>

        {isUS && items.length > 0 && (
          <Box
            sx={{
              width: { xs: "100%", sm: "auto" },
              display: "flex",
              alignItems: "stretch",
              "& > *": { minHeight: ACTION_BTN_HEIGHT },
              "& button": { minHeight: ACTION_BTN_HEIGHT },
            }}
          >
            <InstacartCheckoutButton
              selectedGenericIds={selectedIds}
              context="recipe"
              recipeId={id}
              servings={members}
              lang={urlLang}
            />
          </Box>
        )}
      </Stack>

      {
        saveMsg && (
          <Typography sx={{ mt: 1 }} color="text.secondary">
            {saveMsg}
          </Typography>
        )
      }

      <Divider sx={{ my: 3 }} />

      {/* Instructions */}
      <Typography variant="h5" sx={{ mb: 1 }}>
        {L.instructions}
      </Typography>
      <Box sx={{ fontSize: 17, lineHeight: 1.7 }}>
        {recipe.instructions?.split(/\d+\.\s?/g).filter(Boolean).length > 1 ? (
          <ol>
            {recipe.instructions
              .split(/\d+\.\s?/g)
              .filter(Boolean)
              .map((step, idx) => (
                <li key={idx}>{step.trim()}</li>
              ))}
          </ol>
        ) : (
          <div>{recipe.instructions}</div>
        )}
      </Box>

      {/* Share Bar */}
      <Box sx={{ mt: 2, mb: 4 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 1.2,
            alignItems: "stretch",
            "& .share-btn": {
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              px: 2.2,
              boxShadow: 1,
              width: "100%",
            },
          }}
        >
          <Button
            className="share-btn"
            variant="contained"
            startIcon={<FacebookIcon />}
            onClick={() => handleShare("facebook")}
            sx={{
              background: isDark ? "linear-gradient(90deg,#2d88ff,#1877f2)" : "linear-gradient(90deg,#4e9af1,#1877f2)",
              "&:hover": { filter: "brightness(0.95)" },
            }}
          >
            {t("share_on_facebook") || "Facebook"}
          </Button>

          <Button className="share-btn" variant="outlined" color="primary" startIcon={<WhatsAppIcon />} onClick={() => handleShare("whatsapp")}>
            WhatsApp
          </Button>

          <Button className="share-btn" variant="outlined" color="primary" startIcon={<TelegramIcon />} onClick={() => handleShare("telegram")}>
            Telegram
          </Button>

          <Button className="share-btn" variant="outlined" color="primary" startIcon={<ForumIcon />} onClick={() => handleShare("messenger")}>
            Messenger
          </Button>

          <Button className="share-btn" variant="outlined" color="primary" onClick={() => handleShare("viber")}>
            Viber
          </Button>

          <Button className="share-btn" variant="outlined" color="primary" startIcon={<PinterestIcon />} onClick={() => handleShare("pinterest")}>
            Pinterest
          </Button>

          <Button className="share-btn" variant="text" color="secondary" startIcon={<XIcon />} onClick={() => handleShare("x")}>
            X
          </Button>

          <Button className="share-btn" variant="text" color="secondary" startIcon={<MailOutlineIcon />} onClick={() => handleShare("email")}>
            Email
          </Button>

          <Button className="share-btn" variant="text" color="secondary" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
            {t("copy_link") || "Copy link"}
          </Button>
        </Box>
      </Box>

      {showLoginDialog && <LoginDialog />}

      <Divider sx={{ my: 4 }} />
      <Box id="reviews-section">
        <RecipeReviewsSection recipeId={recipe.id} />
      </Box>
      <Divider sx={{ my: 4 }} />

      {/* Premium gate */}
      <FeatureGateDialog
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        lang={lang}
        isLoggedIn={isLoggedIn}
        require={gateRequire}
        title={lang === "gr" ? t("premium_feature_title") || "Premium λειτουργία" : t("premium_feature_title") || "Premium feature"}
        message={gateMessage || (
          lang === "gr"
            ? t("premium_feature_change_product") || "Η αλλαγή προϊόντος/επιλογών είναι διαθέσιμη μόνο για Premium χρήστες."
            : t("premium_feature_change_product") || "Changing product options is available only for Premium users."
        )}
        onLogin={() => {
          setGateOpen(false);
          navigate(premiumLoginPath);
        }}
        onRegister={() => {
          setGateOpen(false);
          navigate(premiumRegisterPath);
        }}
        onUpgrade={() => {
          setGateOpen(false);
          navigate(withQuery(plansPath, { intent: "premium", plan: "monthly" }));
        }}
      />

      {/* Change product modal (Premium smart matching) */}
      <ChangeProductModal
        open={!!activeModalItem}
        onClose={() => setActiveModalItem(null)}
        offers={activeModalItem?.all_variants || activeModalItem?.offers || []}
        quantity={activeModalItem?.qty || activeModalItem?.required_quantity || 0}
        unit={activeModalItem?.normalized_unit || activeModalItem?.unit || "g"}
        lang={lang}
        bestOfferProductVariantId={activeModalItem?.currentSelectedVariantId || null}
        productName={
          (lang === "el" || lang === "gr"
            ? activeModalItem?.product_name_el || activeModalItem?.name_el
            : activeModalItem?.product_name_en || activeModalItem?.name_en) ||
          activeModalItem?.name
        }
        onSelectOffer={(offer) => {
          const ingredientKey = activeModalItem?.activeIngredientKey || getIngredientKey(activeModalItem);
          const normalizedOffer = normalizeSelectedOffer(offer, activeModalItem);
          if (ingredientKey && normalizedOffer) {
            setSelectedOffersByKey((prev) => ({
              ...prev,
              [ingredientKey]: normalizedOffer,
            }));
            if (process.env.NODE_ENV !== "production") {
              const previousCost = getEffectiveCostValue(activeModalItem);
              const nextCost = getEffectiveCostValue(normalizedOffer);
              if (Math.abs(previousCost - nextCost) > 0.0001) {
                console.info("[RecipeDetails] selected offer changed ingredient cost", {
                  ingredientKey,
                  previousCost,
                  nextCost,
                  variantId: normalizedOffer.product_variant_id,
                });
              }
            }
          }
          setActiveModalItem(null);
        }}
      />
    </Box >
  );
}
